import React, { useContext, useEffect } from "react";
import { initializeTracking, usePageTracking, usePostHog } from "@/hooks/usePostHog";
import AllRoutes, { PathValues } from "@/routing/AllRoutes";

export const DefaultOnboardingStepNameByRoute: {
  [key in PathValues]: string;
} = {
  [AllRoutes.ROOT]: "ROOT",
  [AllRoutes.CONFIG_WIZARD_ROOT]: "Getting Started",
  [AllRoutes.CONNECTIVITY_CHECK]: "Connectivity Check",
  [AllRoutes.CONNECTIVITY_STATUS]: "Connectivity Status",
  [AllRoutes.CONNECTORS_STATUS]: "Connectors Status",
  [AllRoutes.CONNECTOR_EVSEID]: "Connector EVSE ID",
  [AllRoutes.ADD_INFO]: "Additional Info",
  [AllRoutes.INSTALLATION_REPORT]: "Installation Report",
  [AllRoutes.EMAIL_NEWSLETTER]: "Email",
  [AllRoutes.CONNECTIVITY_NO_FOUND]: "Connectivity Not Found",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_GRID]: "Connectivity Troubleshoot grid",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_BE]: "Connectivity Troubleshoot Backend",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_COMM]: "Connectivity Troubleshoot Communication",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_FW]: "Connectivity Troubleshoot Firmware",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_REBOOT]: "Connectivity Troubleshoot Reboot",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_DONE]: "Connectivity Troubleshoot Retry",
  [AllRoutes.CONNECTIVITY_TROUBLESHOOT_UNSUCCESSFUL]: "Connectivity Unsuccessful",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_OFFLINE]: "Connectivity Pre Config Offline",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_FW_RETRY]:
    "Connectivity Pre Config Troubleshoot Firmware",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_GRID]:
    "Connectivity Pre Config Troubleshoot Grid",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_BE]:
    "Connectivity Pre Config Troubleshoot Backend",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_COMM]:
    "Connectivity Pre Config Troubleshoot Communication",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_REBOOT]:
    "Connectivity Pre Config Troubleshoot Reboot",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_DONE]:
    "Connectivity Pre Config Troubleshoot Retry",
  [AllRoutes.CONNECTIVITY_PRE_CONFIG_TROUBLESHOOT_UNSUCCESSFUL]:
    "Connectivity Pre Config Troubleshoot Unsuccessful",
  [AllRoutes.CONNECTORS_TROUBLESHOOT_GROUPING]: "Connectors Troubleshoot Grouping",
  [AllRoutes.CONNECTORS_TROUBLESHOOT_AVAILABLE]: "Connectors Troubleshoot Available",
  [AllRoutes.CONNECTORS_TROUBLESHOOT_FAILED]: "Connectors Troubleshoot Failed"
};

export const getOnboardingStepFromCurrentRoute = () => {
  return DefaultOnboardingStepNameByRoute[window.location.pathname as PathValues] || "Unknown step";
};

export const useTracking = <
  Callback extends FunctionType,
  CallbackArgs extends Parameters<Callback>
>({
  callback,
  eventMessage
}: {
  callback: Callback;
  eventMessage: string | ((...args: CallbackArgs) => string);
}) => {
  const { shouldTrack, onboardingStep } = useTrackingContext();
  const { trackEvent } = usePostHog();

  if (typeof callback !== "function") {
    return callback;
  }

  if (!shouldTrack) {
    return callback;
  }

  return (...args: CallbackArgs) => {
    let message: string;

    if (typeof eventMessage === "function") {
      message = eventMessage(...args);
    } else {
      message = eventMessage;
    }

    trackEvent({
      eventName: message,
      properties: {
        Component: onboardingStep || getOnboardingStepFromCurrentRoute()
      }
    });
    return callback(...args);
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionType = (...args: any[]) => any;

export const withTracking = <
  Props extends { [key in ActionPropName]?: Callback },
  ActionPropName extends keyof Props,
  Callback extends FunctionType,
  ActionArgs extends Parameters<Props[ActionPropName]>
>(
  Component: React.FC<Props>,
  {
    actionAccessor,
    generateMessage
  }: {
    actionAccessor: ActionPropName;
    generateMessage: (props: Props, ...actionArgs: ActionArgs) => string;
  }
) => {
  type TrackedComponentProps = Props & { shouldTrack?: boolean };

  const WrappedComponent: React.FC<TrackedComponentProps> = (props) => {
    const untrackedHandler = props[actionAccessor];

    const trackedHandler = useTracking({
      callback: untrackedHandler,
      eventMessage: (...args: ActionArgs) => generateMessage(props, ...args)
    });

    const actionHandler = props.shouldTrack === false ? untrackedHandler : trackedHandler;

    const componentProps = {
      ...props,
      [actionAccessor]: actionHandler
    };

    return <Component {...componentProps} />;
  };

  return WrappedComponent;
};

interface ITrackingContext {
  shouldTrack?: boolean;
  onboardingStep?: string;
  children?: React.ReactNode;
}

const TrackingContext = React.createContext<ITrackingContext>({
  shouldTrack: true,
  onboardingStep: null
});

export const TrackingContextProvider: React.FC<ITrackingContext> = (props) => {
  const { children, shouldTrack = true, onboardingStep } = props;

  useEffect(initializeTracking, []);
  usePageTracking();

  return (
    <TrackingContext.Provider value={{ shouldTrack, onboardingStep }}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTrackingContext = () => {
  return useContext(TrackingContext);
};
